import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { AriaRole, ReactNode, RefObject } from 'react'

import { cn } from '@/shared/lib/cn'

import { Portal } from './Portal'

/** Separación entre el ancla y el panel, y margen mínimo contra el borde. */
const GAP = 4
const MARGEN = 8

interface Posicion {
  top: number
  left: number
  maxHeight: number
  minWidth: number
}

export interface PopoverProps {
  /** El elemento que lo abre. El panel se posiciona contra él y no cuenta como "afuera". */
  anchor: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  /** `start` alinea los bordes izquierdos; `end`, los derechos. */
  align?: 'start' | 'end'
  /** Al menos tan ancho como el ancla: lo que necesita un desplegable. */
  matchWidth?: boolean
  id?: string
  role?: AriaRole
  'aria-label'?: string
  className?: string
  children: ReactNode
}

/**
 * Un panel anclado a un elemento, que nunca queda recortado.
 *
 * Vive en `body` con `position: fixed`, así que ningún `overflow-hidden` de
 * la tarjeta o la lista que lo contiene puede cortarlo. Abre hacia abajo si
 * entra y hacia arriba si no; horizontalmente se corre lo necesario para no
 * salirse de la pantalla. Sigue al ancla en el scroll y en el resize.
 */
export function Popover({
  anchor,
  open,
  onClose,
  align = 'start',
  matchWidth = false,
  id,
  role,
  className,
  children,
  ...aria
}: PopoverProps) {
  const panel = useRef<HTMLDivElement>(null)
  const [posicion, setPosicion] = useState<Posicion | null>(null)

  const ubicar = useCallback(() => {
    const ancla = anchor.current?.getBoundingClientRect()
    const flotante = panel.current?.getBoundingClientRect()
    if (!ancla || !flotante) return

    const alto = window.innerHeight
    const ancho = window.innerWidth
    const abajo = alto - ancla.bottom - GAP - MARGEN
    const arriba = ancla.top - GAP - MARGEN
    const haciaArriba = flotante.height > abajo && arriba > abajo

    // El alto disponible del lado elegido: una lista larga scrollea adentro del
    // panel en vez de salirse de la pantalla.
    const maxHeight = Math.max(120, haciaArriba ? arriba : abajo)
    const top = haciaArriba
      ? Math.max(MARGEN, ancla.top - GAP - Math.min(flotante.height, maxHeight))
      : ancla.bottom + GAP
    const minWidth = matchWidth ? ancla.width : 0
    const anchoFinal = Math.max(flotante.width, minWidth)
    const deseado = align === 'end' ? ancla.right - anchoFinal : ancla.left
    const left = Math.min(Math.max(MARGEN, deseado), ancho - anchoFinal - MARGEN)

    setPosicion((previa) =>
      previa &&
      previa.top === top &&
      previa.left === left &&
      previa.maxHeight === maxHeight &&
      previa.minWidth === minWidth
        ? previa
        : { top, left, maxHeight, minWidth },
    )
  }, [anchor, align, matchWidth])

  // Antes del primer pintado: el panel nunca aparece en (0, 0) ni salta.
  useLayoutEffect(() => {
    if (!open) {
      setPosicion(null)
      return
    }
    ubicar()

    // `capture`: el scroll de cualquier contenedor, no sólo el de la ventana.
    window.addEventListener('scroll', ubicar, true)
    window.addEventListener('resize', ubicar)
    const observador = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(ubicar)
    if (panel.current) observador?.observe(panel.current)
    return () => {
      window.removeEventListener('scroll', ubicar, true)
      window.removeEventListener('resize', ubicar)
      observador?.disconnect()
    }
  }, [open, ubicar])

  useEffect(() => {
    if (!open) return

    const fuera = (evento: MouseEvent) => {
      const objetivo = evento.target as Node
      if (anchor.current?.contains(objetivo) || panel.current?.contains(objetivo)) return
      onClose()
    }
    const escape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', escape)
    }
  }, [open, onClose, anchor])

  if (!open) return null

  return (
    <Portal>
      <div
        ref={panel}
        id={id}
        role={role}
        {...aria}
        style={{
          top: posicion?.top ?? 0,
          left: posicion?.left ?? 0,
          maxHeight: posicion?.maxHeight,
          minWidth: posicion?.minWidth || undefined,
          visibility: posicion ? undefined : 'hidden',
        }}
        // Por encima de los Sheet (z-50): un popover también se abre desde un panel.
        className={cn(
          'hairline fixed z-[60] overflow-auto rounded-[var(--radius-card)] bg-surface-1',
          className,
        )}
      >
        {children}
      </div>
    </Portal>
  )
}
